import { API_URL } from '../utils/constants';
import { ApiError } from './api';
import { AdminUser, AdminTemplate, TemplateCreateInput, TemplateUpdateInput } from '../types/admin.types';

interface RawAdminUser {
  id: string;
  email: string;
  name?: string;
  is_approved: boolean;
  created_at: string;
}

function normalize(raw: RawAdminUser): AdminUser {
  return {
    id: raw.id,
    email: raw.email,
    name: raw.name,
    isApproved: raw.is_approved,
    createdAt: raw.created_at,
  };
}

async function request<T>(path: string, adminSecret: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_URL ?? ''}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Admin-Secret': adminSecret,
      ...(init?.headers ?? {}),
    },
  });

  if (!response.ok) {
    const data = (await response.json().catch(() => ({}))) as { message?: string; code?: string };
    throw new ApiError(data.message ?? 'Request failed', response.status, data.code);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export const adminService = {
  async listUsers(adminSecret: string): Promise<AdminUser[]> {
    const response = await request<{ users: RawAdminUser[] }>('/api/admin/users', adminSecret);
    return response.users.map(normalize);
  },

  async approveUser(adminSecret: string, userId: string): Promise<AdminUser> {
    const response = await request<{ user: RawAdminUser }>(
      `/api/admin/users/${userId}/approve`,
      adminSecret,
      { method: 'PATCH' }
    );
    return normalize(response.user);
  },

  async deleteUser(adminSecret: string, userId: string): Promise<void> {
    await request<void>(`/api/admin/users/${userId}`, adminSecret, { method: 'DELETE' });
  },

  async listTemplates(adminSecret: string): Promise<AdminTemplate[]> {
    return request<AdminTemplate[]>('/api/admin/templates', adminSecret);
  },

  async createTemplate(adminSecret: string, input: TemplateCreateInput): Promise<AdminTemplate> {
    return request<AdminTemplate>('/api/admin/templates', adminSecret, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  async updateTemplate(adminSecret: string, id: string, input: TemplateUpdateInput): Promise<AdminTemplate> {
    return request<AdminTemplate>(`/api/admin/templates/${id}`, adminSecret, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },

  async deactivateTemplate(adminSecret: string, id: string): Promise<void> {
    await request<void>(`/api/admin/templates/${id}`, adminSecret, { method: 'DELETE' });
  },
};
